import { AdSlotDataValues, AdSlotIdDataInput } from '@mbrtargeting/metatag-shared-types/metatag-core';

/**
 * Refine the search or IDs.
 */
interface AdSlotIdDataSearchOptions {
    /** if false, will show ids that entered the system first, even if those were later overridden. */
    latest?: boolean;
    /** show only ids from a given source */
    source?: string;
}

/**
 * Internal data structure to store the IDs
 */
type AdSlotIdData = AdSlotIdDataInput & {
    timestamp: number;
};

/**
 * Internal storage to save the IDs per slotname.
 */
const slotIdStore: Record<string, AdSlotIdData[]> = {};

/**
 * Stores ids for a given slot.
 *
 * @param slotName the slotName to store ids for
 * @param data source and values to store
 */
export const setIdsForAdSlot = (slotName: string, data: AdSlotIdDataInput) => {
    // create record for adslot if it does not exist and save data into array
    // note: timestamp can be exactly the same when function is called too fast
    (slotIdStore[slotName] ||= []).push({ ...data, timestamp: Date.now() });
};

/**
 * Search for all ids on a given slot.
 *
 * @param slotName the slotName to search ids for
 * @param options Options allow to further refine the search, either by only considering IDs from a given source and/or by searching for ids that entered the system first (istead of last)
 */
export const getIdsForAdSlot = (slotName: string, options?: AdSlotIdDataSearchOptions): Partial<AdSlotDataValues> => {
    const { latest = true, source = undefined } = options || {};
    return (slotIdStore[slotName] || [])
        // pairing data and index to use index in sort function
        .map((data, index) => ({ data, index }))
        // filter by source if one is given or just pass through otherwise
        .filter(({ data }) => !source || data.source === source)
        // sort the records depending if we want the first or last value
        .sort((a, b) => latest ? 0 : b.index - a.index)
        // merge all values into an result object after all records were sorted
        .reduce((result, { data }) => ({ ...result, ...data.values }), {});
};
